/**
 * F42 (B1) — Admin venue management table.
 *
 * Route: /venues
 * Permission: venues:read
 *
 * Extends the F10 catalogue table with the F42 ops surface: a KPI Stat row
 * (total / active / suspended / claimed / flagged from the list `meta`), richer
 * filters (F09 v2 verification `verified | unverified | open_request` +
 * flagged-only), trigram search, row-selection + a bulk-action toolbar shell
 * (gated `venues:bulk`; the action wiring is B2's BulkPanel), and the F09 v2
 * verification cell (inert tick + owner-state badge + open-request marker from
 * `verificationState` / `openVerificationRequest`) plus suspension / flagged
 * Badges. Clicking a row navigates to /venues/:id.
 *
 * Data source: the F42 `adminVenuesApi` (aliased to its fixture in tests).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Button,
  Dialog,
  EmptyState,
  MapView,
  SearchInput,
  SearchableSelect,
  SegmentedControl,
  Select,
  Skeleton,
  Table,
  TierBadge,
  Tooltip,
  useToast,
  type TableColumn,
  useDebouncedValue,
} from '@bravemove/ui-web';
import { SportIcon, UIIcon, type SportName } from '@bravemove/design-tokens/icons';
import {
  ADMIN_VENUE_VERIFICATION_FILTERS,
  VENUE_STATUSES,
  VENUE_TIERS,
  VENUE_CLAIM_STATUSES,
  SPORT_NAME_VALUES,
  type AdminVenueListRow,
  type AdminVenueListMeta,
  type AdminVenueVerificationFilter,
  type BulkVenueAction,
} from '@bravemove/types';
import { BulkPanel } from './venues/components/BulkPanel';
import { VerificationStateCell } from '../components/verification/EntityVerificationCard';
import { adminVenuesApi, type AdminVenueListQuery } from '../api/adminVenues';
import { adminFetch } from '../auth/apiClient';
import { getGeo } from '../api/geo';
import { useAuth } from '../auth/useAuth';
import { translateApiError } from '../auth/errors.js';
import { PageHeader } from '../components/PageHeader';
import { AdminPagination } from '../components/AdminPagination';
import { VenueStatusGuide } from '../components/VenueStatusGuide';

const PAGE_LIMIT = 25;

const GMAPS_API_KEY = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined) ?? '';
/** Lebanon bounding box — initial map viewport. */
const LEBANON_BBOX = { west: 35.1, south: 33.05, east: 36.7, north: 34.7 } as const;

/** Short hover hints for the KPI cards (whitespace-nowrap — keep them brief). */
const KPI_HINTS: Record<string, string> = {
  total: 'All venues · every status',
  active: 'Live & published · bookable',
  suspended: 'Hidden by an admin',
  claimed: 'Owned by a B2B account',
  flagged: 'Needs admin review',
};

/** Slim venue shape returned by GET /admin/venues/map-points (MapView-compatible).
 * Declared as a `type` (not `interface`) so it gains an implicit index signature
 * and stays assignable to MapView's indexed `VenueListItem`. */
type MapVenue = {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly lat: string;
  readonly lng: string;
  readonly tier: 'tier_1' | 'tier_2' | 'tier_3';
  readonly status: string;
  readonly sports: ReadonlyArray<{ readonly sportName: SportName; readonly isPrimary: boolean }>;
};

/** Caza dropdown option from GET /geo/cazas (name + bbox for the auto-zoom). */
interface CazaOption {
  readonly code: string;
  readonly name: string;
  readonly bbox: {
    readonly minLat: number;
    readonly minLng: number;
    readonly maxLat: number;
    readonly maxLng: number;
  };
}

const SOURCE_SHORT: Record<string, string> = {
  google: 'Google',
  osm: 'OSM',
  'google+osm': 'Google',
};

function titleCaseSport(s: string): string {
  return s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Row avatar: a sport icon. A Google photo here would be a paid call per row. */
function VenueRowAvatar({ row }: { row: AdminVenueListRow }) {
  const primary = row.sports.find((s) => s.isPrimary)?.sportName ?? row.sports[0]?.sportName;
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-[13px] font-semibold text-accent">
      {primary ? (
        <SportIcon name={primary as SportName} size={18} color="currentColor" />
      ) : (
        (row.name.trim()[0] ?? 'V').toUpperCase()
      )}
    </div>
  );
}

/** Compact sport tags for a table row (primary highlighted, +N overflow). */
function VenueSportsChips({ sports }: { sports: AdminVenueListRow['sports'] }) {
  if (sports.length === 0) return <span className="text-[11px] text-ink-3">—</span>;
  const shown = sports.slice(0, 3);
  const extra = sports.length - shown.length;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {shown.map((s) => (
        <span
          key={s.sportName}
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10.5px] font-medium ${
            s.isPrimary ? 'bg-accent-soft text-accent' : 'bg-surface-2 text-ink-2'
          }`}
        >
          {titleCaseSport(s.sportName)}
        </span>
      ))}
      {extra > 0 ? <span className="text-[10.5px] text-ink-3">+{extra}</span> : null}
    </div>
  );
}

const STATUS_TONES: Record<string, 'success' | 'warning' | 'neutral' | 'danger' | 'info'> = {
  active: 'success',
  draft: 'neutral',
  suspended: 'warning',
  closed: 'danger',
  merged: 'info',
};

const CLAIM_TONES: Record<string, 'success' | 'warning' | 'neutral' | 'danger' | 'info'> = {
  claimed: 'success',
  claim_pending: 'warning',
  unclaimed: 'neutral',
};

/** F09 v2 (E41) — narrow the `?verification=` URL param to the frozen filter values. */
function asVerificationFilter(raw: string | null): AdminVenueVerificationFilter | null {
  return raw !== null && (ADMIN_VENUE_VERIFICATION_FILTERS as readonly string[]).includes(raw)
    ? (raw as AdminVenueVerificationFilter)
    : null;
}

/** i18n key suffix for each venue verification filter option. */
const VERIFICATION_FILTER_LABEL: Record<AdminVenueVerificationFilter, string> = {
  verified: 'venueFilterVerified',
  unverified: 'venueFilterUnverified',
  open_request: 'venueFilterOpenRequest',
};

function buildQuery(raw: {
  q: string | null;
  status: string | null;
  tier: string | null;
  claimStatus: string | null;
  verification: AdminVenueVerificationFilter | null;
  flagged: boolean;
  caza: string | null;
  sport: string | null;
  page: number;
}): AdminVenueListQuery {
  return {
    perPage: PAGE_LIMIT,
    page: raw.page,
    ...(raw.q ? { q: raw.q } : {}),
    ...(raw.status ? { status: raw.status } : {}),
    ...(raw.tier ? { tier: raw.tier } : {}),
    ...(raw.claimStatus ? { claimStatus: raw.claimStatus } : {}),
    ...(raw.verification ? { verification: raw.verification } : {}),
    ...(raw.flagged ? { flagged: true } : {}),
    ...(raw.caza ? { cazaCode: raw.caza } : {}),
    ...(raw.sport ? { sport: raw.sport } : {}),
  };
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function AdminVenuesPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const auth = useAuth();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const canBulk = auth.adminUser?.permissions?.includes('venues:bulk') ?? false;
  const canWrite = auth.adminUser?.permissions?.includes('venues:write') ?? false;
  const canFlag = auth.adminUser?.permissions?.includes('venues:flag') ?? false;

  const qParam = searchParams.get('q') ?? '';
  // F61 (A4) — the list (an audited read) is fetched once typing pauses, not
  // per keystroke; the input itself stays bound to the URL param.
  const debouncedQ = useDebouncedValue(qParam, 300);
  const statusParam = searchParams.get('status') ?? '';
  const tierParam = searchParams.get('tier') ?? '';
  const claimParam = searchParams.get('claimStatus') ?? '';
  const verificationParam = asVerificationFilter(searchParams.get('verification')) ?? '';
  const flaggedParam = searchParams.get('flagged') === 'true';
  const cazaParam = searchParams.get('caza') ?? '';
  const sportParam = searchParams.get('sport') ?? '';
  const view: 'table' | 'map' = searchParams.get('view') === 'map' ? 'map' : 'table';
  const pageParam = Math.max(Number.parseInt(searchParams.get('page') ?? '1', 10) || 1, 1);

  const [rows, setRows] = useState<readonly AdminVenueListRow[]>([]);
  const [meta, setMeta] = useState<AdminVenueListMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  // Bulk panel (V-04 fix — the toolbar buttons now open B2's BulkPanel).
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState<BulkVenueAction>('suspend');
  // Per-row resolve-flag affordance (V-08 — the list's resolve entry point).
  const [resolveTarget, setResolveTarget] = useState<AdminVenueListRow | null>(null);
  const [resolving, setResolving] = useState(false);

  // Map view geo data. Caza + sport (and the status→state mapping) are shared
  // filters read from the URL, so they apply to BOTH the table list and the map.
  const [mapVenues, setMapVenues] = useState<readonly MapVenue[]>([]);
  const [mapLoading, setMapLoading] = useState(false);
  // Once the map is up it stays up through later reloads: each new one is billed.
  const [mapShown, setMapShown] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [cazaOptions, setCazaOptions] = useState<readonly CazaOption[]>([]);

  // The map's coarse active/inactive/all state derives from the shared status
  // filter. `/admin/venues/map-points` only supports the tri-state
  // (inactive = `status <> 'active'`), so every non-active status filter maps
  // to 'inactive' — pins can't match a single non-active status exactly, but
  // they no longer show ACTIVE pins while the table is filtered to
  // draft/suspended/closed/merged (V-21 fix).
  const mapState: 'all' | 'active' | 'inactive' =
    statusParam === 'active' ? 'active' : statusParam ? 'inactive' : 'all';

  const listRequestSeq = useRef(0);
  const fetchList = useCallback(
    async (opts: AdminVenueListQuery) => {
      // Only the newest request may fill the table: a slow older response must
      // not replace the list the admin is now looking at.
      const seq = ++listRequestSeq.current;
      setLoading(true);
      setRows([]);
      setError(null);
      try {
        const res = await adminVenuesApi.list(opts);
        if (seq !== listRequestSeq.current) return;
        setRows(res.data);
        setMeta(res.meta);
      } catch (err) {
        if (seq !== listRequestSeq.current) return;
        setError(translateApiError(err, t));
      } finally {
        if (seq === listRequestSeq.current) setLoading(false);
      }
    },
    [t],
  );

  const currentQuery = useMemo(
    () =>
      buildQuery({
        q: debouncedQ || null,
        status: statusParam || null,
        tier: tierParam || null,
        claimStatus: claimParam || null,
        verification: verificationParam || null,
        flagged: flaggedParam,
        caza: cazaParam || null,
        sport: sportParam || null,
        page: pageParam,
      }),
    [
      debouncedQ,
      statusParam,
      tierParam,
      claimParam,
      verificationParam,
      flaggedParam,
      cazaParam,
      sportParam,
      pageParam,
    ],
  );

  useEffect(() => {
    // Bulk actions may only reach rows the admin can see. The search is debounced,
    // so rows ticked before this reload would otherwise stay selected once hidden.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedIds(new Set());
    void fetchList(currentQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuery]);

  // Caza dropdown options (F04 /geo/cazas — name + bbox drives the map auto-zoom).
  // The caza filter is shared across both views, so load eagerly on mount.
  useEffect(() => {
    let cancelled = false;
    getGeo<{ data: ReadonlyArray<{ code: string; name: string; bbox: CazaOption['bbox'] }> }>(
      '/geo/cazas',
    )
      .then((res) => {
        if (!cancelled) {
          setCazaOptions(res.data.map((c) => ({ code: c.code, name: c.name, bbox: c.bbox })));
        }
      })
      .catch(() => {
        /* non-fatal — the caza filter just stays empty */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Map geo points — refetched whenever the caza / state filter changes. The
  // admin list rows carry no coordinates, so the map reads the dedicated
  // GET /admin/venues/map-points (returns drafts too, unlike /public/venues).
  useEffect(() => {
    if (view !== 'map' || !GMAPS_API_KEY) return undefined;
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMapLoading(true);
    setMapError(null);
    const qs = new URLSearchParams({ state: mapState });
    if (cazaParam) qs.set('cazaCode', cazaParam);
    if (sportParam) qs.set('sport', sportParam);
    adminFetch<{ data: readonly MapVenue[] }>(`/admin/venues/map-points?${qs.toString()}`)
      .then((res) => {
        if (cancelled) return;
        setMapVenues(res.data);
        setMapShown(true);
      })
      .catch((err: unknown) => {
        if (!cancelled) setMapError(err instanceof Error ? err.message : 'Failed to load map');
      })
      .finally(() => {
        if (!cancelled) setMapLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [view, cazaParam, sportParam, mapState]);

  /** V-08 — resolve a row's review flag from the list (confirm-gated). */
  const handleResolveFlag = async () => {
    if (!resolveTarget) return;
    setResolving(true);
    try {
      await adminVenuesApi.resolveFlag(resolveTarget.id, {});
      toast.push({
        tone: 'success',
        title: t('admin:venue.f42.flagResolve.success', { defaultValue: 'Flag resolved.' }),
      });
      setResolveTarget(null);
      await fetchList(currentQuery);
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    } finally {
      setResolving(false);
    }
  };

  function openBulk(action: BulkVenueAction) {
    setBulkAction(action);
    setBulkOpen(true);
  }

  function setParam(key: string, value: string) {
    setSelectedIds(new Set());
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      // Any filter/search change resets to the first page.
      next.delete('page');
      return next;
    });
  }

  function goToPage(page: number) {
    setSelectedIds(new Set());
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (page <= 1) next.delete('page');
      else next.set('page', String(page));
      return next;
    });
  }

  /** Clear every filter + search (keeps the current Table/Map tab). */
  function clearFilters() {
    setSelectedIds(new Set());
    setSearchParams((prev) => {
      const next = new URLSearchParams();
      const v = prev.get('view');
      if (v) next.set('view', v);
      return next;
    });
  }

  const anyFilterActive = Boolean(
    qParam ||
    statusParam ||
    tierParam ||
    claimParam ||
    verificationParam ||
    flaggedParam ||
    cazaParam ||
    sportParam,
  );

  const statusOptions = useMemo(
    () => [
      { value: '', label: t('admin:venue.status.all') },
      ...VENUE_STATUSES.map((s) => ({ value: s, label: t(`admin:venue.status.${s}`) })),
    ],
    [t],
  );

  const tierOptions = useMemo(
    () => [
      { value: '', label: t('admin:venue.tier.all') },
      ...VENUE_TIERS.map((tier) => ({ value: tier, label: t(`admin:venue.tier.${tier}`) })),
    ],
    [t],
  );

  const claimOptions = useMemo(
    () => [
      { value: '', label: t('admin:venue.claimStatus.all') },
      ...VENUE_CLAIM_STATUSES.map((c) => ({ value: c, label: t(`admin:venue.claimStatus.${c}`) })),
    ],
    [t],
  );

  const verificationOptions = useMemo(
    () => [
      { value: '', label: t('admin:verification.integrations.venueFilterAll') },
      ...ADMIN_VENUE_VERIFICATION_FILTERS.map((v) => ({
        value: v,
        label: t(`admin:verification.integrations.${VERIFICATION_FILTER_LABEL[v]}`),
      })),
    ],
    [t],
  );

  const flaggedOptions = useMemo(
    () => [
      { value: '', label: t('admin:venue.f42.filters.all') },
      { value: 'true', label: t('admin:venue.f42.filters.flaggedOnly') },
    ],
    [t],
  );

  const columns = useMemo<readonly TableColumn<AdminVenueListRow>[]>(
    () => [
      {
        key: 'venue',
        header: t('admin:venue.table.name'),
        cell: (row) => (
          <div className="flex min-w-0 items-center gap-2.5">
            <VenueRowAvatar row={row} />
            <div className="flex min-w-0 flex-col gap-0.5">
              <div className="flex min-w-0 items-center gap-1.5">
                <span className="truncate text-[13px] font-medium text-ink">{row.name}</span>
                {row.source ? (
                  <Badge tone="info" size="sm">
                    {SOURCE_SHORT[row.source] ?? row.source}
                  </Badge>
                ) : null}
              </div>
              <span className="truncate font-mono text-[11px] text-ink-3">/v/{row.slug}</span>
            </div>
          </div>
        ),
      },
      {
        key: 'location',
        header: t('admin:venue.table.location'),
        cell: (row) => (
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate text-[12.5px] text-ink">{row.cityName ?? row.cityCode}</span>
            {row.governorateName ? (
              <span className="truncate text-[11px] text-ink-3">{row.governorateName}</span>
            ) : null}
          </div>
        ),
      },
      {
        key: 'sports',
        header: t('admin:venue.table.sports', { defaultValue: 'Sports' }),
        cell: (row) => <VenueSportsChips sports={row.sports} />,
      },
      {
        key: 'tier',
        header: t('admin:venue.table.tier'),
        cell: (row) => (
          <TierBadge
            tier={row.tier as 'tier_1' | 'tier_2' | 'tier_3'}
            size="sm"
            label={t(`admin:venue.tier.${row.tier}`, { defaultValue: row.tier })}
          />
        ),
      },
      {
        key: 'claim',
        header: t('admin:venue.table.claimStatus'),
        cell: (row) => (
          <Badge tone={CLAIM_TONES[row.claimStatus] ?? 'neutral'} size="sm">
            {t(`admin:venue.claimStatus.${row.claimStatus}`, { defaultValue: row.claimStatus })}
          </Badge>
        ),
      },
      {
        key: 'verification',
        header: t('admin:venue.f42.filters.verificationStatus'),
        // F09 v2 (§13 row 21) — tick + owner state + open request, never the
        // deprecated 4-valued `verificationStatus`.
        cell: (row) => (
          <VerificationStateCell
            entityType="venue"
            state={row.verificationState}
            openRequest={row.openVerificationRequest}
            testId={`admin-venue-verification-${row.id}`}
          />
        ),
      },
      {
        key: 'status',
        header: t('admin:venue.table.status'),
        cell: (row) => (
          <div className="flex flex-wrap items-center gap-1">
            <Badge tone={STATUS_TONES[row.status] ?? 'neutral'} size="sm">
              {t(`admin:venue.status.${row.status}`, { defaultValue: row.status })}
            </Badge>
            {row.flaggedForReviewAt !== null ? (
              <>
                <Badge tone="danger" size="sm" data-testid={`admin-venues-flagged-badge-${row.id}`}>
                  {t('admin:venue.f42.badge.flagged')}
                </Badge>
                {canFlag ? (
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      setResolveTarget(row);
                    }}
                    data-testid={`admin-venues-resolve-flag-${row.id}`}
                  >
                    {t('admin:venue.f42.flagResolve.cta', { defaultValue: 'Resolve' })}
                  </Button>
                ) : null}
              </>
            ) : null}
          </div>
        ),
      },
      {
        key: 'createdAt',
        header: t('admin:venue.table.createdAt'),
        cell: (row) => (
          <span className="text-[12px] text-ink-3">{formatDate(row.createdAt, locale)}</span>
        ),
      },
    ],
    [t, locale, canFlag],
  );

  const kpis: readonly { key: string; label: string; value: number }[] = useMemo(() => {
    const m = meta;
    return [
      { key: 'total', label: t('admin:venue.f42.kpi.total'), value: m?.total ?? 0 },
      { key: 'active', label: t('admin:venue.f42.kpi.active'), value: m?.active ?? 0 },
      { key: 'suspended', label: t('admin:venue.f42.kpi.suspended'), value: m?.suspended ?? 0 },
      { key: 'claimed', label: t('admin:venue.f42.kpi.claimed'), value: m?.claimed ?? 0 },
      { key: 'flagged', label: t('admin:venue.f42.kpi.flagged'), value: m?.flagged ?? 0 },
    ];
  }, [meta, t]);

  // Map viewport bounds — the selected caza's bbox auto-zooms; Lebanon-wide else.
  const selectedCaza = cazaParam ? cazaOptions.find((c) => c.code === cazaParam) : undefined;
  const mapBounds = selectedCaza
    ? {
        west: selectedCaza.bbox.minLng,
        south: selectedCaza.bbox.minLat,
        east: selectedCaza.bbox.maxLng,
        north: selectedCaza.bbox.maxLat,
      }
    : LEBANON_BBOX;
  // Shared location-filter dropdown options (caza from /geo/cazas; sport from the
  // canonical 41-value sport list). Both filter the table AND the map.
  const cazaSelectOptions = [
    { value: '', label: t('admin:venue.f42.map.allCazas', { defaultValue: 'All cazas' }) },
    ...cazaOptions.map((c) => ({ value: c.code, label: c.name })),
  ];
  const sportSelectOptions = [
    { value: '', label: t('admin:venue.f42.map.allSports', { defaultValue: 'All sports' }) },
    ...SPORT_NAME_VALUES.map((s) => ({ value: s, label: titleCaseSport(s) })),
  ];

  // BulkPanel input — names for the selected ids (falls back to the id when a
  // selected row scrolled out of the current page's rows).
  const selectedVenues = useMemo(
    () =>
      [...selectedIds].map((id) => ({
        id,
        name: rows.find((r) => r.id === id)?.name ?? id,
      })),
    [selectedIds, rows],
  );

  return (
    <div className="space-y-4" data-testid="admin-venues-page">
      <PageHeader
        title={t('admin:venue.pageTitle.list')}
        actions={
          canWrite ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate('/venues/new')}
              data-testid="admin-venues-new-venue"
            >
              {t('admin:venue.actions.newVenue')}
            </Button>
          ) : undefined
        }
      />

      {/* KPI summary — compact inline strip */}
      <section
        className="flex flex-wrap items-stretch divide-x divide-hairline overflow-hidden rounded-xl border border-hairline bg-surface"
        data-testid="admin-venues-kpi"
      >
        {kpis.map((k) => (
          <Tooltip
            key={k.key}
            side="top"
            className="min-w-[110px] flex-1"
            content={t(`admin:venue.f42.kpi.hint.${k.key}`, {
              defaultValue: KPI_HINTS[k.key] ?? '',
            })}
          >
            <div
              data-testid={`admin-venues-kpi-${k.key}`}
              className="flex cursor-help flex-col gap-1 px-4 py-2.5"
            >
              <span className="font-mono text-[10px] font-bold uppercase tracking-wider text-ink-3">
                {k.label}
              </span>
              <span className="text-[19px] font-bold leading-none text-ink">
                {k.value.toLocaleString()}
              </span>
            </div>
          </Tooltip>
        ))}
      </section>

      <VenueStatusGuide />

      {/* Controls — search + view tabs on top, then the shared filter row.
          Every filter AND the active tab are URL-synced, so a refresh restores
          the exact same view + results. Caza + Sport are location filters that
          apply to BOTH the table and the map. */}
      <div className="space-y-2.5" data-testid="admin-venues-toolbar">
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-[220px] flex-1">
            <SearchInput
              value={qParam}
              onChange={(e) => setParam('q', e.target.value)}
              placeholder={t('admin:venue.filters.search')}
            />
          </div>
          <SegmentedControl<'table' | 'map'>
            value={view}
            onChange={(v) => setParam('view', v === 'map' ? 'map' : '')}
            options={[
              { value: 'table', label: t('admin:venue.f42.view.table', { defaultValue: 'Table' }) },
              { value: 'map', label: t('admin:venue.f42.view.map', { defaultValue: 'Map' }) },
            ]}
            aria-label={t('admin:venue.f42.view.aria', { defaultValue: 'View mode' })}
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="w-[150px]">
            <Select
              aria-label={t('admin:venue.filters.status')}
              value={statusParam}
              onChange={(e) => setParam('status', e.target.value)}
              options={statusOptions}
              data-testid="admin-venues-filter-status"
            />
          </div>
          <div className="w-[160px]">
            <Select
              aria-label={t('admin:venue.f42.map.caza', { defaultValue: 'Caza' })}
              value={cazaParam}
              onChange={(e) => setParam('caza', e.target.value)}
              options={cazaSelectOptions}
              data-testid="admin-venues-filter-caza"
            />
          </div>
          <div className="w-[150px]">
            <SearchableSelect
              aria-label={t('admin:venue.f42.map.sport', { defaultValue: 'Sport' })}
              value={sportParam}
              onChange={(v) => setParam('sport', v)}
              options={sportSelectOptions}
              placeholder={t('admin:venue.f42.map.allSports', { defaultValue: 'All sports' })}
              searchPlaceholder={t('common:search', { defaultValue: 'Search' })}
              data-testid="admin-venues-filter-sport"
            />
          </div>
          <div className="w-[130px]">
            <Select
              aria-label={t('admin:venue.filters.tier')}
              value={tierParam}
              onChange={(e) => setParam('tier', e.target.value)}
              options={tierOptions}
              data-testid="admin-venues-filter-tier"
            />
          </div>
          <div className="w-[160px]">
            <Select
              aria-label={t('admin:venue.filters.claimStatus')}
              value={claimParam}
              onChange={(e) => setParam('claimStatus', e.target.value)}
              options={claimOptions}
              data-testid="admin-venues-filter-claim"
            />
          </div>
          <div className="w-[160px]">
            <Select
              aria-label={t('admin:venue.f42.filters.verificationStatus')}
              value={verificationParam}
              onChange={(e) => setParam('verification', e.target.value)}
              options={verificationOptions}
              data-testid="admin-venues-filter-verification"
            />
          </div>
          <div className="w-[120px]">
            <Select
              aria-label={t('admin:venue.f42.filters.flagged')}
              value={flaggedParam ? 'true' : ''}
              onChange={(e) => setParam('flagged', e.target.value)}
              options={flaggedOptions}
              data-testid="admin-venues-filter-flagged"
            />
          </div>
          {anyFilterActive ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              data-testid="admin-venues-clear-filters"
            >
              {t('admin:venue.f42.filters.clear', { defaultValue: 'Clear' })}
            </Button>
          ) : null}
        </div>
      </div>

      {/* Bulk-action toolbar — only rendered when the actor can bulk + has a
          selection. Each entry button opens B2's BulkPanel with the action
          preselected (V-04 fix — the buttons were no-ops). */}
      {canBulk && selectedIds.size > 0 ? (
        <div
          className="flex flex-wrap items-center gap-2 rounded-lg border border-outline bg-surface-2 px-3 py-2"
          data-testid="admin-venues-bulk-toolbar"
        >
          <span className="text-[13px] font-medium text-ink" data-testid="admin-venues-bulk-count">
            {t('admin:venue.f42.bulk.selected', { count: selectedIds.size })}
          </span>
          <div className="flex flex-wrap gap-2 ms-auto">
            <Button
              variant="secondary"
              size="xs"
              onClick={() => openBulk('suspend')}
              data-testid="admin-venues-bulk-suspend"
            >
              {t('admin:venue.f42.bulk.suspend')}
            </Button>
            <Button
              variant="secondary"
              size="xs"
              onClick={() => openBulk('restore')}
              data-testid="admin-venues-bulk-restore"
            >
              {t('admin:venue.f42.bulk.restore')}
            </Button>
            <Button
              variant="secondary"
              size="xs"
              onClick={() => openBulk('flag')}
              data-testid="admin-venues-bulk-flag"
            >
              {t('admin:venue.f42.bulk.flag')}
            </Button>
            <Button
              variant="ghost"
              size="xs"
              onClick={() => setSelectedIds(new Set())}
              data-testid="admin-venues-bulk-clear"
            >
              {t('admin:venue.f42.bulk.clear')}
            </Button>
          </div>
        </div>
      ) : null}

      {/* Bulk panel — Drawer with action picker + mandatory reason + confirm +
          Idempotency-Key + the per-venue partial-success result. */}
      <BulkPanel
        open={bulkOpen}
        selected={selectedVenues}
        initialAction={bulkAction}
        onClose={() => setBulkOpen(false)}
        onDone={() => {
          setSelectedIds(new Set());
          void fetchList(currentQuery);
        }}
      />

      {/* Per-row resolve-flag confirm (V-08). */}
      <Dialog
        open={resolveTarget !== null}
        onClose={() => setResolveTarget(null)}
        onConfirm={() => void handleResolveFlag()}
        loading={resolving}
        title={t('admin:venue.f42.flagResolve.title', { defaultValue: 'Resolve this flag?' })}
        description={t('admin:venue.f42.flagResolve.body', {
          defaultValue:
            'Marks the review flag on this venue as resolved. The flag history stays in the audit log.',
        })}
        confirmLabel={t('admin:venue.f42.footer.resolveFlag')}
        cancelLabel={t('admin:common.cancel')}
      >
        <p className="text-[13px] font-medium text-ink">{resolveTarget?.name}</p>
      </Dialog>

      {view === 'map' ? (
        <div data-testid="admin-venues-map" className="flex flex-col gap-3">
          {/* Location filters (caza / sport / status) now live in the shared
              toolbar above and apply here too — the map just shows a live count. */}
          <div className="flex items-center justify-end text-[12px] text-ink-3">
            {mapLoading
              ? t('admin:venue.f42.map.loading', { defaultValue: 'Loading…' })
              : `${mapVenues.length} ${t('admin:venue.f42.map.countSuffix', {
                  defaultValue: 'venues · pins by sport',
                })}`}
          </div>

          <div className="rounded-2xl border border-hairline overflow-hidden shadow-[0_10px_30px_-18px_rgba(0,0,0,0.22)]">
            {!GMAPS_API_KEY ? (
              <Banner tone="warning">
                {t('admin:venue.f42.map.noKey', {
                  defaultValue:
                    'Set VITE_GOOGLE_MAPS_API_KEY in apps/admin/.env (with the Maps JavaScript API enabled) to show the map.',
                })}
              </Banner>
            ) : mapError ? (
              <Banner tone="danger">{mapError}</Banner>
            ) : !mapShown ? (
              <div className="p-4">
                <Skeleton className="h-[520px] w-full rounded-lg" />
              </div>
            ) : (
              <MapView
                apiKey={GMAPS_API_KEY}
                venues={mapVenues}
                initialBounds={mapBounds}
                cameraRequest={{
                  id: selectedCaza?.code ?? 'all',
                  type: 'bounds',
                  bounds: mapBounds,
                }}
                height={560}
                ariaLabel={t('admin:venue.f42.map.aria', { defaultValue: 'Venues map' })}
                onVenueClick={(v) => navigate(`/venues/${v.id}`)}
              />
            )}
          </div>
        </div>
      ) : (
        <>
          {error ? <Banner tone="danger">{error}</Banner> : null}

          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }, (_, i) => (
                <Skeleton key={i} className="h-14 w-full rounded-lg" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<UIIcon name="pin" size={36} color="currentColor" />}
              title={t('admin:venue.table.empty')}
            />
          ) : (
            <Table
              data={rows as AdminVenueListRow[]}
              columns={columns as TableColumn<AdminVenueListRow>[]}
              rowKey={(row) => row.id}
              {...(canBulk
                ? {
                    selectable: true,
                    selectedIds,
                    onSelectionChange: setSelectedIds,
                  }
                : {})}
              onRowPress={(row) => navigate(`/venues/${row.id}`)}
              empty={t('admin:venue.table.empty')}
            />
          )}

          {meta && !loading && rows.length > 0 ? (
            <AdminPagination
              pagination={meta.pagination}
              shownCount={rows.length}
              onChange={goToPage}
              testId="admin-venues-pagination"
            />
          ) : null}
        </>
      )}
    </div>
  );
}
